import { useCallback } from 'react';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
import { capitalize } from 'lodash';
import type { SyncSettingResponse } from '@timeedit/calendar-integration-api-contract';
import {
  Button,
  EditableTable,
  type EditableTableProps,
} from '@timeedit/ui-components';
import { Flex } from '@timeedit/ui-components/antd';
import { intlPrefix } from './MS365SyncSettingsDetails';

type Props = EditableTableProps<SyncSettingResponse> & {
  onCreate: () => void;
  createDisabled?: boolean;
};

export function MS365SyncSettingsList({
  onCreate,
  createDisabled = false,
  ...props
}: Props) {
  const footer = useCallback(
    () => (
      <Flex style={{ width: '100%' }} justify="space-between">
        <Flex gap="small" justify="flex-start">
          <Button
            size="small"
            type="primary"
            onClick={onCreate}
            disabled={createDisabled}
          >
            Create new
          </Button>
        </Flex>
      </Flex>
    ),
    [onCreate, createDisabled]
  );

  const title = useTableTitle({ total: props.dataSource?.length ?? 0 });

  return (
    <EditableTable<SyncSettingResponse>
      columns={columns}
      footer={footer}
      pagination={false}
      title={title}
      {...props}
    />
  );
}

const columns = [
  {
    title: getText(`${intlPrefix}.form.name.label`),
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: getText(`${intlPrefix}.form.sync_type.label`),
    dataIndex: 'syncType',
    key: 'syncType',
    render: (type: string) => {
      return capitalize(type);
    },
  },
  {
    title: getText(`${intlPrefix}.form.sync_mode.label`),
    dataIndex: ['syncMode', 'mode'],
    key: 'syncMode',
    render: (mode: string) => {
      switch (mode) {
        case 'TE_IN':
          return getText(`${intlPrefix}.form.sync_mode.te_in.label`);
        case 'TE_OUT':
          return getText(`${intlPrefix}.form.sync_mode.te_out.label`);
        case 'TE_TWO_WAY':
          return getText(`${intlPrefix}.form.sync_mode.te_two_way.label`);
        default:
          return mode;
      }
    },
  },
  {
    title: getText(`${intlPrefix}.form.sync_period.label`),
    dataIndex: ['syncPeriod', 'type'],
    key: 'syncPeriod',
    render: (type: string) => {
      switch (type) {
        case 'STATIC':
          return getText(`${intlPrefix}.form.sync_period.static.label`);
        case 'DYNAMIC':
          return getText(`${intlPrefix}.form.sync_period.relative.label`);
        default:
          return type;
      }
    },
  },
];
